import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import WorkPage from './pages/WorkPage';
import ProjectPage from './pages/ProjectPage';
import AboutPage from './pages/AboutPage';
import ConnectPage from './pages/ConnectPage';
import FloatingTextBar from './components/FloatingTextBar';
import PageTransition from './components/PageTransition';
import LoadingScreen from './components/LoadingScreen';
import ScrollToTop from './components/ScrollToTop';
import DitheringBackground from './components/DitheringBackground';
import StructuredData, { generateWebSiteSchema } from './components/StructuredData';
import { projects } from './data/projects';
import useAssetPreloader from './hooks/useAssetPreloader';
import './App.css';

function App() {
  // Build a list of key assets (images / videos) used across the site
  const assetUrls = useMemo(() => {
    const urls = new Set();

    projects.forEach((project) => {
      if (project.image) {
        urls.add(project.image);
      }

      if (Array.isArray(project.images)) {
        project.images.forEach((img) => urls.add(img));
      }
    });

    return Array.from(urls);
  }, []);

  const { progress } = useAssetPreloader(assetUrls, { minimumDuration: 3000 });

  // Set current year in footer
  useEffect(() => {
    const yearElement = document.getElementById('year');
    if (yearElement) {
      yearElement.textContent = new Date().getFullYear();
    }
  }, []);

  // While assets are loading, show the splash screen
  if (progress < 100) {
    return <LoadingScreen progress={progress} />;
  }

  return (
    <HelmetProvider>
      <StructuredData data={generateWebSiteSchema()} />
      <Router>
        <ScrollToTop />
        <DitheringBackground />
        <PageTransition>
          <div className="App">
            <Header />
            <FloatingTextBar />
            
            <main className="main-content">
              <Routes>
                <Route path="/" element={<Navigate to="/work" replace />} />
                <Route path="/work" element={<WorkPage />} />
                <Route path="/work/:projectSlug" element={<ProjectPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/connect" element={<ConnectPage />} />
              </Routes>
            </main>
          </div>
        </PageTransition>
      </Router>
    </HelmetProvider>
  );
}

export default App;